/**
 * StatusBadge - Appointment status indicator
 * Nordic Sanctuary design: Calm colors, clear meaning
 */

import { useTranslation } from "react-i18next";
import { CheckCircle, Clock, XCircle, Video, CalendarCheck } from "lucide-react";

type Status = "scheduled" | "confirmed" | "in_progress" | "completed" | "cancelled" | "no_show";

interface StatusBadgeProps {
  status: Status;
  size?: "sm" | "md" | "lg";
}

type StatusConfig = {
  icon: typeof Clock;
  bgClass: string;
  textClass: string;
  iconClass: string;
};

const statusConfig: Record<Status, StatusConfig> = {
  scheduled: {
    icon: Clock,
    bgClass: "bg-[var(--sage-100)]",
    textClass: "text-[var(--sage-700)]",
    iconClass: "text-[var(--sage-600)]",
  },
  confirmed: {
    icon: CalendarCheck,
    bgClass: "bg-[oklch(0.94_0.04_145)]",
    textClass: "text-[oklch(0.35_0.06_145)]",
    iconClass: "text-[oklch(0.45_0.08_145)]",
  },
  in_progress: {
    icon: Video,
    bgClass: "bg-[oklch(0.94_0.06_145)]",
    textClass: "text-[oklch(0.30_0.06_145)]",
    iconClass: "text-[oklch(0.40_0.08_145)] animate-pulse",
  },
  completed: {
    icon: CheckCircle,
    bgClass: "bg-[var(--stone-100)]",
    textClass: "text-[var(--stone-600)]",
    iconClass: "text-[var(--stone-500)]",
  },
  cancelled: {
    icon: XCircle,
    bgClass: "bg-[oklch(0.95_0.02_25)]",
    textClass: "text-[oklch(0.45_0.12_25)]",
    iconClass: "text-[oklch(0.55_0.15_25)]",
  },
  no_show: {
    icon: XCircle,
    bgClass: "bg-[oklch(0.95_0.02_25)]",
    textClass: "text-[oklch(0.45_0.12_25)]",
    iconClass: "text-[oklch(0.55_0.15_25)]",
  },
};

function getStatusLabel(
  t: (key: string) => string,
  status: Status
): string {
  switch (status) {
    case "scheduled":
      return t("status.scheduled");
    case "confirmed":
      return t("status.confirmed");
    case "in_progress":
      return t("status.inProgress");
    case "completed":
      return t("status.completed");
    case "cancelled":
      return t("status.cancelled");
    case "no_show":
      return t("status.noShow");
    default:
      return t("status.scheduled");
  }
}

const sizeClasses = {
  sm: "px-2.5 py-1 text-xs gap-1",
  md: "px-3.5 py-1.5 text-sm gap-1.5",
  lg: "px-4 py-2 text-base gap-2",
};

const iconSizes = {
  sm: "w-3 h-3",
  md: "w-4 h-4",
  lg: "w-5 h-5",
};

export function StatusBadge({ status, size = "md" }: StatusBadgeProps) {
  const { t } = useTranslation("appointment");
  const config = statusConfig[status] ?? statusConfig.scheduled;
  const Icon = config.icon;

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full
        ${sizeClasses[size]}
        ${config.bgClass}
        ${config.textClass}
      `}
    >
      <Icon className={`${iconSizes[size]} ${config.iconClass}`} aria-hidden="true" />
      {getStatusLabel(t, status)}
    </span>
  );
}
